import { useLanguage } from '../../../core/i18n/hooks/use-language';
import { BASE_BACKEND_QUERY_KEY, BASE_BACKEND_URL } from '../../backend/constants';
import { QueryOptions, useAuthenticatedQuery } from '../../backend/hooks/use-authenticated-query';
import {
  GetAddressMutationResponse,
  GetArticleNotFoundResponse,
  GetCreateNewUserForExistingCustomerResponse,
  GetDeleteUserResponse,
  GetInquiryAboutArticleResponse,
  GetNewDeliveryAddressResponse,
  GetOrderSampleSheetResponse,
  GetServiceRequestResponse,
} from '../../backend/openapi/api-types';
import { AuthenticatedFormType } from '../types';
import { createSearchParams } from '../../backend/services/create-search-params';

type ResponseTypeMap = {
  articleNotFound: GetArticleNotFoundResponse;
  addressMutation: GetAddressMutationResponse;
  inquiryAboutArticle: GetInquiryAboutArticleResponse;
  deleteUser: GetDeleteUserResponse;
  newDeliveryAddress: GetNewDeliveryAddressResponse;
  createNewUserForExistingCustomer: GetCreateNewUserForExistingCustomerResponse;
  orderSampleSheet: GetOrderSampleSheetResponse;
  serviceRequest: GetServiceRequestResponse;
};

const requestFormUrls = (
  formType: keyof ResponseTypeMap,
  queryParams?: Record<string, string | number | null>,
): string => {
  const baseUrls: Record<keyof ResponseTypeMap, string> = {
    articleNotFound: `${BASE_BACKEND_URL}/request-forms/article-not-found`,
    addressMutation: `${BASE_BACKEND_URL}/request-forms/address-mutation`,
    inquiryAboutArticle: `${BASE_BACKEND_URL}/request-forms/inquiry-about-article`,
    deleteUser: `${BASE_BACKEND_URL}/request-forms/delete-user`,
    newDeliveryAddress: `${BASE_BACKEND_URL}/request-forms/new-delivery-address`,
    createNewUserForExistingCustomer: `${BASE_BACKEND_URL}/request-forms/create-new-user-for-existing-customer`,
    orderSampleSheet: `${BASE_BACKEND_URL}/request-forms/order-sample-sheet`,
    serviceRequest: `${BASE_BACKEND_URL}/request-forms/service-request`,
  } as const;

  const baseUrl = baseUrls[formType];

  if (queryParams) {
    const searchParams = createSearchParams(queryParams);
    return `${baseUrl}?${searchParams.toString()}`;
  }

  return baseUrl;
};

export const useAuthenticatedFormQuery = <T extends AuthenticatedFormType>(
  formType: T,
  queryParam?: Record<string, string | number | null>,
) => {
  const language = useLanguage();
  const queryOptions: QueryOptions<ResponseTypeMap[T]> = {
    options: {
      queryKey: [BASE_BACKEND_QUERY_KEY, language, formType, queryParam],
    },
    url: requestFormUrls(formType, queryParam),
  };
  return useAuthenticatedQuery<ResponseTypeMap[T]>(queryOptions);
};
